import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { bind, displayFormatTime, Enums, IActiveElement, LineGraph } from 'Mri.Web.Common';
import * as React from 'react';
import { Collapse } from 'reactstrap';
import { ChartData, LegendItem } from 'chart.js';

export interface IMinMaxItemProps {
    readonly minMaxModel: Mri.Web.Internal.Controllers.Displays.MinMaxModel;
    readonly serialNumber: string;
}

@observer
export class MinMaxItem extends React.Component<IMinMaxItemProps> {
    public render() {
        return (
            <>
                <tr>
                    <td scope="col" className="align-middle">
                        <span className="d-inline-block mb-0 align-middle">
                            {Enums.toString(Enums.MeasurementId, this.props.minMaxModel.sensorId)}
                        </span>
                    </td>
                    <td scope="col" className="align-middle">
                        {this.props.minMaxModel.lifetimeMin}
                    </td>
                    <td scope="col" className="align-middle">
                        {this.props.minMaxModel.lifetimeMax}
                    </td>
                    <td scope="col" className="align-middle">
                        {this.props.minMaxModel.dailyMinMaxs[0].min}
                    </td>
                    <td scope="col" className="align-middle">
                        {this.props.minMaxModel.dailyMinMaxs[0].max}
                    </td>
                    <td scope="col" className="align-middle p-2">
                        <a onClick={this.onToggle}>
                            <div className="card bg-gray-darker spark-line">
                                <LineGraph
                                    data={this.sparkData}
                                    displayLabels={false}
                                    hideDots
                                    hideLines
                                    legendPosition="top"
                                    onClick={this.zoomOnData}
                                    onLegendClick={this.legendClick}
                                    enablePan
                                    enableZoom
                                    fontColor="#fff"
                                    gridColor="#fff"
                                    hideXaxis
                                    hideYaxis
                                />
                            </div>
                        </a>
                    </td>
                </tr>

                <tr className={this.collapsed ? 'd-none' : 'text-center'}>
                    <td colSpan={4}>
                        <Collapse isOpen={!this.collapsed}>
                            <div className="col-md-8 d-inline-block card py-2 bg-gray-darker rounded p-3">
                                <LineGraph
                                    data={this.yearData}
                                    hideDots
                                    hideLines
                                    legendPosition="top"
                                    onClick={this.zoomOnData}
                                    onLegendClick={this.legendClick}
                                    enablePan
                                    enableZoom
                                    fontColor="#fff"
                                    gridColor="#fff"
                                />
                            </div>
                        </Collapse>
                    </td>
                </tr>
            </>
        );
    }

    @bind
    @action
    private zoomOnData(activeElements: IActiveElement[], event: any) {
        if (activeElements.length) {
            const clickedDatasetIndex = activeElements[0].datasetIndex;
            const clickedElementindex = activeElements[0].index;

            if (this.yearData.datasets) {
                const filtered = this.yearData.datasets[clickedDatasetIndex];
                this.hiddenLabels = this.yearData.datasets.filter(d => d.label != filtered.label).map(d => d.label!);
                filtered.hidden = true;
            }
        } else if (!this.legendClicked) {
            this.hiddenLabels = [];
            //(this.chartRef!.chartInstance as any).resetZoom(); TODO Fix Zoom
        }

        this.legendClicked = false;
    }

    @bind
    @action
    private legendClick(event: any, legendItem: LegendItem) {
        this.legendClicked = true;

        const legend = legendItem;

        if (legend.hidden) {
            this.hiddenLabels.splice(this.hiddenLabels.indexOf(legend.text!), 1);
        } else {
            this.hiddenLabels.push(legend.text!);
        }
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @computed
    private get sparkData(): ChartData<'line'> {
        const data = this.props.minMaxModel.dailyMinMaxs.slice(0, 6).reverse();

        return {
            labels: data.map(d => displayFormatTime(d.date, 'EEEE', this.props.serialNumber)),
            datasets: [
                {
                    type: 'line',
                    label: 'Min',
                    borderColor: '#2d8ba3',
                    backgroundColor: '#2d8ba3',
                    borderWidth: 0.8,
                    fill: false,
                    data: data.map(d => d.min ?? 0),
                },
                {
                    type: 'line',
                    label: 'Max',
                    borderColor: '#de254d',
                    backgroundColor: '#de254d',
                    borderWidth: 0.8,
                    fill: false,
                    data: data.map(d => d.max ?? 0),
                },
            ],
        };
    }

    @computed
    private get yearData(): ChartData<'line'> {
        const data = this.props.minMaxModel.dailyMinMaxs.reverse().slice(0, 346);

        return {
            labels: data.map(d => displayFormatTime(d.date, 'M/d/yy', this.props.serialNumber)),
            datasets: [
                {
                    type: 'line',
                    label: 'Min',
                    borderColor: '#2d8ba3',
                    backgroundColor: '#2d8ba3',
                    borderWidth: 0.8,
                    fill: false,
                    data: data.map(d => d.min ?? 0),
                },
                {
                    type: 'line',
                    label: 'Max',
                    borderColor: '#de254d',
                    backgroundColor: '#de254d',
                    borderWidth: 0.8,
                    fill: false,
                    data: data.map(d => d.max ?? 0),
                },
            ],
        };
    }

    @observable
    private collapsed = true;
    @observable
    private hiddenLabels: string[] = [];
    @observable
    private legendClicked: boolean = false;
}
